import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import Seo from '../components/common/Seo';
import toast from 'react-hot-toast';
import { ArrowLeft, Trophy, Users, Calendar, Shield } from 'lucide-react';
import { useTheme } from '../contexts/ThemeContext';

const SPORT_META = {
  basketball: {
    title: 'Basketball Tournament App — Brackets, Squads & Live Scoring',
    description: 'Organize or join basketball tournaments near you. Build your squad, track live scores, and connect with players. Free on iOS & Android.',
    image: 'https://www.kynaixsports.com/images/app_assets/social_previews/social-preview-basketball.jpg',
  },
  soccer: {
    title: 'Soccer Tournament App — Brackets, Squads & Live Scoring',
    description: 'Organize or join soccer tournaments near you. Build your squad, track live scores, and connect with players. Free on iOS & Android.',
    image: 'https://www.kynaixsports.com/images/app_assets/social_previews/social-preview-soccer.jpg',
  },
  volleyball: {
    title: 'Volleyball Tournament App — Brackets, Squads & Live Scoring',
    description: 'Organize or join volleyball tournaments near you. Build your squad, track live scores, and connect with players. Free on iOS & Android.',
    image: 'https://www.kynaixsports.com/images/app_assets/social_previews/social-preview-volleyball.jpg',
  },
  tennis: {
    title: 'Tennis Tournament App — Brackets, Squads & Live Scoring',
    description: 'Organize or join tennis tournaments near you. Build your squad, track live scores, and connect with players. Free on iOS & Android.',
    image: 'https://www.kynaixsports.com/images/app_assets/social_previews/social-preview-tennis.jpg',
  },
  pickleball: {
    title: 'Pickleball Tournament App — Brackets, Squads & Live Scoring',
    description: 'Organize or join pickleball tournaments near you. Build your squad, track live scores, and connect with players. Free on iOS & Android.',
    image: 'https://www.kynaixsports.com/images/app_assets/social_previews/social-preview-pickleball.jpg',
  },
  cricket: {
    title: 'Cricket Tournament App — Brackets, Squads & Live Scoring',
    description: 'Organize or join cricket tournaments near you. Build your squad, track live scores, and connect with players. Free on iOS & Android.',
    image: 'https://www.kynaixsports.com/images/app_assets/social_previews/social-preview-cricket.jpg',
  },
  'ping-pong': {
    title: 'Ping Pong Tournament App — Brackets, Squads & Live Scoring',
    description: 'Organize or join ping pong tournaments near you. Build your squad, track live scores, and connect with players. Free on iOS & Android.',
    image: 'https://www.kynaixsports.com/images/app_assets/social_previews/social-preview-pingpong.jpg',
  },
  badminton: {
    title: 'Badminton Tournament App — Brackets, Squads & Live Scoring',
    description: 'Organize or join badminton tournaments near you. Build your squad, track live scores, and connect with players. Free on iOS & Android.',
    image: 'https://www.kynaixsports.com/images/app_assets/social_previews/social-preview-badminton.jpg',
  },
};
import { sportTypeAPI } from '../services/api';
import { teamAPI } from '../teams/services';
import { tournamentAPI } from '../tournaments/services/tournamentApi';
import { useSquads } from '../squads/hooks/useSquads';
import { btn } from '../utils/buttonStyles';

export default function SportPage() {
  const { sportSlug } = useParams();
  const navigate = useNavigate();
  const { theme } = useTheme();

  const [sport, setSport] = useState(null);
  const [teams, setTeams] = useState([]);
  const [tournaments, setTournaments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const { groups: squads, fetchGroups } = useSquads(false);

  useEffect(() => {
    fetchSportData();
  }, [sportSlug]);

  const fetchSportData = async () => {
    try {
      setLoading(true);
      setError(null);

      const sportsResult = await sportTypeAPI.getAll();
      const sportsData = sportsResult?.data || sportsResult || [];

      const matchedSport = sportsData.find(s => {
        const sportName = s.sport_name || s.sportName || s.name || '';
        const slug = sportName.toLowerCase().replace(/\s+/g, '-');
        return slug === sportSlug || sportName.toLowerCase() === sportSlug;
      });

      if (!matchedSport) {
        setError('Sport not found');
        return;
      }

      setSport(matchedSport);

      const sportId = matchedSport.id;

      // Fetch all three filtered by sport in parallel
      const [teamsResult, tournamentsResult] = await Promise.all([
        teamAPI.getAll({ sport: sportId }).catch(() => ({ data: [] })),
        tournamentAPI.getAll({ sportTypeId: sportId }).catch(() => ({ data: [] })),
      ]);

      const teamsData = teamsResult?.data || teamsResult || [];
      setTeams(Array.isArray(teamsData) ? teamsData.slice(0, 6) : []);

      const tournamentsData = tournamentsResult?.data || tournamentsResult || [];
      setTournaments(Array.isArray(tournamentsData) ? tournamentsData.slice(0, 4) : []);

      // Squads via hook
      fetchGroups({ sportTypeId: sportId, limit: 6 });

    } catch (err) {
      console.error('Error fetching sport data:', err);
      setError('Failed to load sport information');
      toast.error('Failed to load sport');
    } finally {
      setLoading(false);
    }
  };

  const getSportIcon = (sportName) => {
    const name = (sportName || '').toLowerCase();
    if (name.includes('basketball')) return '🏀';
    if (name.includes('volleyball')) return '🏐';
    if (name.includes('pickleball')) return '🏓';
    if (name.includes('tennis')) return '🎾';
    if (name.includes('soccer') || name.includes('football')) return '⚽';
    if (name.includes('baseball')) return '⚾';
    if (name.includes('cricket')) return '🏏';
    if (name.includes('golf')) return '⛳';
    if (name.includes('swim')) return '🏊';
    if (name.includes('hockey')) return '🏒';
    return '🏆';
  };

  if (loading) {
    return (
      <div className={`min-h-screen ${theme.background} flex items-center justify-center`}>
        <div className="w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (error || !sport) {
    return (
      <div className={`min-h-screen ${theme.background} py-6 px-4`}>
        <div className="max-w-6xl mx-auto text-center py-12">
          <Trophy className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <h2 className={`text-xl font-semibold ${theme.text} mb-2`}>Sport not found</h2>
          <p className={`${theme.textSecondary} mb-6`}>{error || 'The sport you are looking for does not exist.'}</p>
          <button
            onClick={() => navigate('/')}
            className={`${btn.base} ${btn.lg} ${theme.buttonPrimary}`}
          >
            Go Home
          </button>
        </div>
      </div>
    );
  }

  const sportName = sport.sport_name || sport.sportName || sport.name;
  const sportIcon = getSportIcon(sportName);
  const meta = SPORT_META[sportSlug] || {
    title: `${sportName} Tournament App — Brackets, Squads & Live Scoring`,
    description: `Organize or join ${sportName} tournaments near you. Build your squad, track live scores, and connect with players. Free on iOS & Android.`,
    image: 'https://www.kynaixsports.com/images/app_assets/social_previews/social-preview.jpg',
  };
  const pageUrl = `https://www.kynaixsports.com/sport/${sportSlug}`;

  return (
    <div className={`min-h-screen ${theme.background} py-6 px-4 sm:px-6 lg:px-8`}>
      {/* Title, description, canonical, og:* and twitter:* now go through <Seo>,
          which rewrites the tags index.html already ships. Rendering them here
          through Helmet instead ADDED a second copy of each without removing the
          static one, so every /sport/* page served two conflicting canonicals —
          its own and the homepage's. Google discards ambiguous canonical pairs,
          which is why these six sitemap URLs never indexed. The JSON-LD below is
          safe to keep in Helmet: there is no competing script tag to duplicate. */}
      <Seo title={meta.title} description={meta.description} image={meta.image} path={`/sport/${sportSlug}`} />
      <Helmet>
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "WebPage",
          "name": meta.title,
          "description": meta.description,
          "url": pageUrl,
          "isPartOf": { "@id": "https://www.kynaixsports.com/#website" },
          "publisher": { "@id": "https://www.kynaixsports.com/#organization" }
        })}</script>
      </Helmet>
      <div className="max-w-6xl mx-auto">

        {/* Header */}
        <div className="flex items-center mb-8">
          <button
            onClick={() => navigate(-1)}
            className={`mr-4 p-2 ${theme.cardHover} rounded-lg transition-colors`}
          >
            <ArrowLeft className={`w-6 h-6 ${theme.textSecondary}`} />
          </button>
          <div className="flex items-center gap-x-4">
            <span className="text-6xl">{sportIcon}</span>
            <div>
              <h1 className={`text-4xl font-bold ${theme.text}`}>{sportName}</h1>
              {sport.played_by && (
                <p className={`${theme.textSecondary} mt-1`}>Played by: {sport.played_by}</p>
              )}
            </div>
          </div>
        </div>

        {/* About */}
        <div className={`${theme.card} rounded-xl ${theme.shadow} p-6 mb-8`}>
          <h2 className={`text-2xl font-bold ${theme.text} mb-4`}>About {sportName}</h2>
          <p className={`${theme.textSecondary} text-lg`}>
            {sport.description || `${sportName} is an exciting sport enjoyed by players around the world.`}
          </p>
        </div>

        {/* Quick Stats */}
        <div className="grid grid-cols-3 gap-4 mb-8">
          <div className={`${theme.card} rounded-xl p-4 text-center ${theme.shadow}`}>
            <Users className="w-8 h-8 mx-auto mb-2 text-blue-500" />
            <div className={`text-2xl font-bold ${theme.text}`}>{teams.length}</div>
            <div className={`text-sm ${theme.textMuted}`}>Teams</div>
          </div>
          <div className={`${theme.card} rounded-xl p-4 text-center ${theme.shadow}`}>
            <Trophy className="w-8 h-8 mx-auto mb-2 text-yellow-500" />
            <div className={`text-2xl font-bold ${theme.text}`}>{tournaments.length}</div>
            <div className={`text-sm ${theme.textMuted}`}>Tournaments</div>
          </div>
          <div className={`${theme.card} rounded-xl p-4 text-center ${theme.shadow}`}>
            <Shield className="w-8 h-8 mx-auto mb-2 text-purple-500" />
            <div className={`text-2xl font-bold ${theme.text}`}>{squads.length}</div>
            <div className={`text-sm ${theme.textMuted}`}>Squads</div>
          </div>
        </div>

        {/* Teams */}
        {teams.length > 0 && (
          <div className={`${theme.card} rounded-xl ${theme.shadow} p-6 mb-8`}>
            <div className="flex items-center justify-between mb-4">
              <h2 className={`text-2xl font-bold ${theme.text} flex items-center`}>
                <Users className="w-6 h-6 mr-2 text-blue-500" />
                Teams
              </h2>
              <button onClick={() => navigate('/teams')} className="text-blue-600 hover:underline text-sm">
                View All
              </button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {teams.map((team) => {
                const teamName = team.team_name || team.teamName;
                return (
                  <div
                    key={team.id}
                    className={`${theme.backgroundSecondary} rounded-lg p-4 ${theme.cardHover} cursor-pointer transition-colors`}
                    onClick={() => navigate(`/teams/${team.id}`)}
                  >
                    <div className="flex items-center gap-x-3">
                      <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center text-white font-bold text-lg">
                        {teamName?.charAt(0)?.toUpperCase() || 'T'}
                      </div>
                      <div>
                        <h4 className={`font-semibold ${theme.text}`}>{teamName}</h4>
                        <p className={`text-sm ${theme.textSecondary}`}>
                          {team.max_members || team.maxMembers || 0} members
                        </p>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Squads */}
        {squads.length > 0 && (
          <div className={`${theme.card} rounded-xl ${theme.shadow} p-6 mb-8`}>
            <div className="flex items-center justify-between mb-4">
              <h2 className={`text-2xl font-bold ${theme.text} flex items-center`}>
                <Shield className="w-6 h-6 mr-2 text-purple-500" />
                Squads
              </h2>
              <button onClick={() => navigate('/squads')} className="text-blue-600 hover:underline text-sm">
                View All
              </button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {squads.map((squad) => (
                <div
                  key={squad.id}
                  className={`${theme.backgroundSecondary} rounded-lg p-4 ${theme.cardHover} cursor-pointer transition-colors`}
                  onClick={() => navigate(`/squads/${squad.id}`)}
                >
                  <div className="flex items-center gap-x-3">
                    <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-purple-500 to-pink-600 flex items-center justify-center text-white font-bold text-lg">
                      {(squad.name || squad.groupName)?.charAt(0)?.toUpperCase() || 'S'}
                    </div>
                    <div>
                      <h4 className={`font-semibold ${theme.text}`}>{squad.name || squad.groupName}</h4>
                      <p className={`text-sm ${theme.textSecondary}`}>
                        {squad.memberCount || 0} members
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Tournaments */}
        {tournaments.length > 0 && (
          <div className={`${theme.card} rounded-xl ${theme.shadow} p-6 mb-8`}>
            <div className="flex items-center justify-between mb-4">
              <h2 className={`text-2xl font-bold ${theme.text} flex items-center`}>
                <Trophy className="w-6 h-6 mr-2 text-yellow-500" />
                Tournaments
              </h2>
              <button onClick={() => navigate('/tournaments')} className="text-blue-600 hover:underline text-sm">
                View All
              </button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {tournaments.map((tournament) => (
                <div
                  key={tournament.id}
                  className={`${theme.backgroundSecondary} rounded-lg p-4 ${theme.cardHover} cursor-pointer transition-colors`}
                  onClick={() => navigate(`/tournament/${tournament.id}`)}
                >
                  <h4 className={`font-semibold ${theme.text} mb-2`}>
                    {tournament.tournamentname || tournament.name}
                  </h4>
                  <div className="flex items-center text-sm">
                    <span className={`flex items-center ${theme.textSecondary}`}>
                      <Calendar className="w-4 h-4 mr-1" />
                      {tournament.start_date ? new Date(tournament.start_date).toLocaleDateString() : 'TBD'}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Empty state — nothing for this sport yet */}
        {teams.length === 0 && squads.length === 0 && tournaments.length === 0 && (
          <div className={`${theme.card} rounded-xl ${theme.shadow} p-8 text-center mb-8`}>
            <span className="text-5xl mb-4 block">{sportIcon}</span>
            <h3 className={`text-lg font-semibold ${theme.text} mb-2`}>No activity yet for {sportName}</h3>
            <p className={`${theme.textSecondary}`}>Be the first to create a team, squad, or tournament!</p>
          </div>
        )}

        {/* Call to Action */}
        <div className={`${theme.card} rounded-xl ${theme.shadow} p-8 text-center`}>
          <h2 className={`text-2xl font-bold ${theme.text} mb-4`}>Ready to Play {sportName}?</h2>
          <p className={`${theme.textSecondary} mb-6`}>
            Join a squad, find a team, or enter a tournament to get started!
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <button
              onClick={() => navigate('/squads')}
              className={`${btn.base} ${btn.lg} ${theme.buttonPrimary}`}
            >
              Find a Squad
            </button>
            <button
              onClick={() => navigate('/teams')}
              className="px-6 py-3 border border-blue-500 text-blue-600 rounded-xl hover:bg-blue-50 dark:hover:bg-blue-950 transition-colors font-medium"
            >
              Browse Teams
            </button>
            <button
              onClick={() => navigate('/tournaments')}
              className="px-6 py-3 border border-yellow-500 text-yellow-600 rounded-xl hover:bg-yellow-50 dark:hover:bg-yellow-950 transition-colors font-medium"
            >
              View Tournaments
            </button>
          </div>
        </div>

      </div>
    </div>
  );
}
